import * as React from "react";
import { Survey, fieldValidation, SurveyQuestionConfig, ScoredSurveyQuestionOption, SurveyQuestionOption, assertNever, CustomSurveyQuestion, LanguageCode, stringUtil, SurveyQuestion } from "../../../../../../../amorg-common";
import { surveyQuestionOptionObjectValidation } from "../../../../../../../amorg-common/modules/objectValidation/surveyQuestionOptionObjectValidation";
import { Button } from "../../../../../../../designSystem/button/Button";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { TagEditor } from "../../../../../../../designSystem/compositeComponents/TagEditor";
import { OnOff } from "../../../../../../../designSystem/inputs/OnOff";
import { TextArea } from "../../../../../../../designSystem/inputs/TextArea";
import { Label } from "../../../../../../../designSystem/Label";
import { Modal } from "../../../../../../../designSystem/Modal";
import { QuestionConfigEditorDraft, QuestionConfigEditor } from "./QuestionConfigEditor";
import clone from "clone";
import { createSurveyQuestion, updateSurveyQuestion } from "../../../../../../../api/surveyClientApi";
import { QuestionOptionEditorDraft } from "./CustomQuestionOptionEditor";
import { InputMessage } from "../../../../../../../designSystem/InputMessage";

export type QuestionEditorDraft = {
    title: string,
    selectedIndicators: string[],
    //selectedThemeIndex: number,
    canComment: boolean,
    config:QuestionConfigEditorDraft,
    mode: 'new' | 'edit',
    conditionalDisplay?: {
        triggerValues: string[],
    },
};

type QuestionEditModalProps = {

    disabled?: boolean,
    errorMessage?: string,
    survey:Survey,
    themeIndex: number,

    draft: QuestionEditorDraft | null,

    onDraftChange: (draft: QuestionEditorDraft) => void,
    onClose: () => void,
    onSave: (draft: QuestionEditorDraft) => void,
};

export function QuestionEditModal(props: QuestionEditModalProps): JSX.Element
{
    if(!props.draft)
        return <></>;

    const [internalErrorMessage, setInternalErrorMessage] = React.useState("");

    async function handleSave ()
    {
        if(!props.draft)
            return;

        if (!fieldValidation.surveyQuestion.title(props.draft.title))
        {
            setInternalErrorMessage(`Ugyldig titel. Skal udfyldes med maks. 400 (${props.draft.title.length}) tegn.`);
            return;
        }

        if(!fieldValidation.surveyQuestion.indicators(props.draft.selectedIndicators))
        {
            setInternalErrorMessage(`Ugyldige indikatorer. Hver indikator skal være unik og indeholde maks. 50 tegn (ingen specialtegn eller mellerum).`);
            return;
        }

        if(props.draft.config.type === "default")
        {
            setInternalErrorMessage("");
            props.onSave({
                ...props.draft,
            });
        }
        else if(props.draft.config.type === "custom")
        {
            const validationObject: SurveyQuestionConfig = 
                props.draft.config.optionConfig.scored
                ?   {
                        type: "custom",
                        scored: true,
                        options: props.draft.config.optionConfig.options.map((option) => ({
                            text: option.text,
                            value: option.value,
                            score: option.score,
                            translations:[]
                        } satisfies ScoredSurveyQuestionOption))
                    }
                :   {
                        type: "custom",
                        scored: false,
                        options: props.draft.config.optionConfig.options.map((option) => ({
                            text: option.text,
                            value: option.value,
                            translations:[]
                        } satisfies SurveyQuestionOption)),
                    };

            if(!surveyQuestionOptionObjectValidation.validateConfigOptions(validationObject, []))
            {
                setInternalErrorMessage("En eller flere ugyldige svarmuligheder. Hver mulighed skal have unik værdi og tekst, og teksten må maks. være 200 tegn.");
                return;
            }

            setInternalErrorMessage("");
            props.onSave({
                ...props.draft,
            });
        }
        else if(props.draft.config.type === "multiple-choice")
        {
            const validationObject: SurveyQuestionConfig = {
                type: "multiple-choice",
                options: props.draft.config.optionConfig.options.map((option) => ({
                    text: option.text,
                    value: option.value,
                    translations:[]
                }))
            };

            if(!surveyQuestionOptionObjectValidation.validateConfigOptions(validationObject, []))
            {
                setInternalErrorMessage("En eller flere ugyldige svarmuligheder. Hver mulighed skal have unik værdi og tekst, og teksten må maks. være 200 tegn.");
                return;
            }

            setInternalErrorMessage("");
            props.onSave({
                ...props.draft,
            });
        }
    }

    return (
        <Modal
            title={props.draft.mode === 'edit' ? "Rediger spørgsmål" : "Nyt spørgsmål"}
            showModal={props.draft !== null}
            onCancel={props.onClose}
            content={
                <Flex flexDirection="column" gap={16}>

                    {props.errorMessage && <InputMessage inErrorState>{props.errorMessage}</InputMessage>}

                    {internalErrorMessage !== "" &&
                        <InputMessage inErrorState>{internalErrorMessage}</InputMessage>
                    }

                    <TextArea
                        value={props.draft.title}
                        minRows={4}
                        onChange={(value) => {
                            props.onDraftChange({ ...props?.draft as QuestionEditorDraft, title: value });
                        }}
                        ariaLabel="Spørgsmålstekst"
                        disabled={props.disabled} 
                    />

                    <QuestionConfigEditor disabled={props.disabled} 
                        disableTypeChange={props.draft.mode === 'edit'} 
                        draft={props.draft.config} 
                        onChange={(questionConfig) => props.onDraftChange({ ...props?.draft as QuestionEditorDraft, config: questionConfig })} />

                    <Flex alignItems="center" gap={8}>
                        <span>Vis kommentarfelt</span>
                        <OnOff active={props.draft.canComment} onClick={(active) => props.onDraftChange({ ...props?.draft as QuestionEditorDraft, canComment: active })} disabled={props.disabled} />
                    </Flex>

                    <Flex flexDirection="column" gap={8}>
                        <Label>Indikatorer</Label>
                        <IndicatorEditor
                            survey={props.survey}
                            selectedIndicators={props.draft.selectedIndicators}
                            onChange={(selectedIndicators) => props.onDraftChange({ ...props?.draft as QuestionEditorDraft, selectedIndicators })} />
                    </Flex>


                    <Flex justifyContent="space-between">
                        <Button variant="text" onClick={props.onClose} text="Annuller" />
                        <Button variant="filled" onClick={handleSave} text="Gem" disabled={props.disabled} />
                    </Flex>
                </Flex>
            }
            />
    );
}

type IndicatorEditorProps = {
    survey: Survey,
    selectedIndicators:string[],
    onChange:(indicators:string[])=>void
};
function IndicatorEditor(props:IndicatorEditorProps): JSX.Element
{
    const tags:string[] = React.useMemo(() => {
        const allQuestionIndicators = props.survey.themes.flatMap(t => t.questions.flatMap(q => q.indicators));
        const uniqueIndicators = [...new Set([...allQuestionIndicators, ...props.selectedIndicators])].sort((a, b) => a.localeCompare(b));
        return uniqueIndicators;
    }, [props.survey, props.selectedIndicators]);

    function handleTagChange(tags: string[])
    {
        if(fieldValidation.surveyQuestion.indicators(tags))
        {
            props.onChange(tags);
        }
    }

    return (
        <TagEditor
            availableTags={tags}
            texts={{
                selectTagOption: "Vælg indikator",
                addTextPlaceholder: "Ny indikator...",
                addnewTagOption: "Opret ny indikator...",
                noTagsSelectedText: "Ingen indikatorer valgt"
            }}
            selectedTags={props.selectedIndicators} 
            validator={(val) => fieldValidation.surveyQuestion.indicators([val])}
            disabled={false}
            onSelectionChange={handleTagChange} 
        />
    );
}

export function NewQuestionEditModal(props: { 
    showModal: boolean,
    themeIndex: number,
    survey: Survey,
    onSurveyUpdated: () => void,
    onClose: () => void
}): JSX.Element
{
    const [draft, setDraft] = React.useState<QuestionEditorDraft | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string>("");

    React.useEffect(() => {
        if (props.showModal && props.themeIndex>=0 && props.themeIndex < props.survey.themes.length)
        {
            const allQuestionTitles = props.survey.themes.flatMap((theme) => theme.questions.map((question) => question.title));
            const name = stringUtil.generateNextSequenceValue('Nyt spørgsmål', allQuestionTitles);
            setDraft({
                title: name,
                selectedIndicators: [],
                // selectedThemeIndex: props.themeIndex,
                canComment: true,
                config: { type: "default" },
                mode: 'new'
            });
        }
    }, [props.showModal, props.themeIndex]);

    async function handleSaveQuestion(draft: QuestionEditorDraft)
    {
        let config:SurveyQuestionConfig | null = null;
        if (draft.config.type === 'default')
        {
            config = { type: 'default', invertScore: false };
        }
        else if (draft.config.type === 'custom')
        {
            config = draft.config.optionConfig.scored
                ?   {
                        type: "custom",
                        scored: true,
                        options: draft.config.optionConfig.options.map((option) => ({
                            text: option.text,
                            value: option.value,
                            score: option.score,
                            translations: []
                        }))
                    } satisfies SurveyQuestionConfig
                :   {
                        type: "custom",
                        scored: false,
                        options: draft.config.optionConfig.options.map((option) => ({
                            text: option.text,
                            value: option.value,
                            translations: []
                        }))
                    } satisfies SurveyQuestionConfig;
        }
        else if (draft.config.type === 'multiple-choice')
        {
            config = {
                type: "multiple-choice",
                options: draft.config.optionConfig.options.map((option) => ({
                    text: option.text,
                    value: option.value,
                    translations: []
                }))
            } satisfies SurveyQuestionConfig;
        }
        else {
            assertNever(draft.config);
            return;
        }
           
        const result = await createSurveyQuestion(
            props.survey.id, 
            props.themeIndex, 
            draft.selectedIndicators,
            draft.title,
            draft.canComment,
            config,
            draft.conditionalDisplay
        );

        if(result.isSuccess)
        {
            setErrorMessage("");
            props.onSurveyUpdated();
        }
        else {
            setErrorMessage(result.errorMessage ?? "Fejl ved oprettelse af spørgsmål");
        }
    }

    return (
        <QuestionEditModal
            errorMessage={errorMessage}
            survey={props.survey}
            themeIndex={props.themeIndex}
            draft={draft}
            // No use for opening new question model is editing is disabled
            disabled={false}
            onDraftChange={(draft) => setDraft(draft)}
            onClose={props.onClose}
            onSave={handleSaveQuestion} />
    );
}

export function EditQuestionEditModal(props: { 
    showModal: boolean,
    themeIndex: number,
    questionIndex: number,
    selectedLanguageCode: LanguageCode,
    survey: Survey,
    onSurveyUpdated: () => void,
    onClose: () => void
}): JSX.Element
{
    const [draft, setDraft] = React.useState<QuestionEditorDraft | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string>("");

    React.useEffect(() => {
        const theme = props.survey.themes[props.themeIndex];
        const question = theme?.questions[props.questionIndex];

        if (props.showModal && question)
        {
            setDraft(buildDraftFromQuestion(question));
        }
    }, [props.showModal, props.themeIndex, props.questionIndex]);

    function buildDraftFromQuestion(question: SurveyQuestion): QuestionEditorDraft
    {
        const title = props.selectedLanguageCode === props.survey.languageCode
            ? question.title
            : question.translations.find(t => t.languageCode === props.selectedLanguageCode)?.text ?? question.title;

        if(question.type === "default")
        {
            return {
                title,
                selectedIndicators: question.indicators,
                canComment: question.canComment,
                config: { type: "default" },
                mode: 'edit',
                conditionalDisplay: question.conditionalDisplay
            };
        }
        else if(question.type === "custom")
        {
            const shared = {
                title,
                selectedIndicators: question.indicators,
                canComment: question.canComment,
                mode: 'edit',
                conditionalDisplay: question.conditionalDisplay
            };

            const draftOptions: { originalIndex: number | null, text: string, value: string }[] = question.options.map((option, index) => ({
                originalIndex: index,
                text: props.selectedLanguageCode === props.survey.languageCode
                    ? option.text
                    : option.translations.find(t => t.languageCode === props.selectedLanguageCode)?.text ?? option.text,
                value: option.value
            }));

            if(question.scored)
            {
                return {...shared,
                    config: {
                        type: "custom",
                        optionConfig: {
                            scored: true,
                            options: draftOptions.map((option, index) => ({
                                ...option,
                                score: question.options[index].score,
                            }))
                        }
                    },
                    mode: 'edit'
                };
            }
            else {
                return {...shared,
                    config: {
                        type: "custom",
                        optionConfig: {
                            scored: false,
                            options: draftOptions
                        }
                    },
                    mode: 'edit'
                };
            }
        }    
        else if(question.type === "multiple-choice")
        {
            const shared = {
                title,
                selectedIndicators: question.indicators,
                canComment: question.canComment,
                mode: 'edit',
                conditionalDisplay: question.conditionalDisplay
            };

            const draftOptions: { originalIndex: number | null, text: string, value: string }[] = question.options.map((option, index) => ({
                originalIndex: index,
                text: props.selectedLanguageCode === props.survey.languageCode
                    ? option.text
                    : option.translations.find(t => t.languageCode === props.selectedLanguageCode)?.text ?? option.text,
                value: option.value
            }));

            return {...shared,
                config: {
                    type: "multiple-choice",
                    optionConfig: {
                        scored: false,
                        options: draftOptions
                    }
                },
                mode: 'edit'
            };
        }    
        else
        {
            assertNever(question);
        }
    }

    function buildOptionsFromDraft(draft:QuestionOptionEditorDraft):SurveyQuestionOption[] | ScoredSurveyQuestionOption[]
    {
        const updatedOptionData:SurveyQuestionOption[] = [];

        for (let i=0; i<draft.options.length; i++)
        {
            const draftOption = draft.options[i];
            // New option
            if(draftOption.originalIndex === null)
            {
                updatedOptionData.push({
                    text: draftOption.text,
                    value: draftOption.value,
                    // add translations for all languages
                    translations: props.survey.translationLanguages.map((lang) => ({ languageCode: lang, text: draftOption.text }))
                });
            }
            // Existing option
            else {
                const originalOption = (props.survey.themes[props.themeIndex].questions[props.questionIndex] as CustomSurveyQuestion).options[draftOption.originalIndex];
                // Working on default language, so update the default text and keep translations for other languages
                if(props.selectedLanguageCode === props.survey.languageCode)
                {
                    updatedOptionData.push({
                        text: draftOption.text,
                        value: draftOption.value,
                        translations:originalOption.translations.map(t => ({ languageCode: t.languageCode, text: t.text })),
                    });
                }
                // Working on a translation, so keep the default text and other translations, but update the translation for the selected language
                else {
                    updatedOptionData.push({
                        text: originalOption.text,
                        value: draftOption.value,
                        translations:originalOption.translations.map(t => ({ 
                            languageCode: t.languageCode, 
                            text: t.languageCode === props.selectedLanguageCode ? draftOption.text : t.text })),
                    });
                }
            }
        }

        if(draft.scored)
        {
            return updatedOptionData.map((option, index) => ({
                ...option,
                score: draft.options[index].score
            })) satisfies ScoredSurveyQuestionOption[];
        }
        else
        {
            return updatedOptionData;
        }
    }

    async function handleSaveQuestion(draft: QuestionEditorDraft)
    {
        if(draft === null)
            return;
        
        let config:SurveyQuestionConfig = { type: 'default', invertScore: false };
        if (draft.config.type === 'default')
        {
            // No additional config needed for default type
        }
        else if (draft.config.type === 'custom')
        {
            const options = buildOptionsFromDraft(draft.config.optionConfig);
            config = {
                type: 'custom',
                scored: draft.config.optionConfig.scored,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                options:options as unknown as any
            } satisfies SurveyQuestionConfig;
        }
        else if (draft.config.type === 'multiple-choice')
        {
            const options = buildOptionsFromDraft(draft.config.optionConfig);
            config = {
                type: 'multiple-choice',
                options: options as unknown as SurveyQuestionOption[]
            } satisfies SurveyQuestionConfig;
        }
        else
        {
            assertNever(draft.config);
        }

        const originalQuestion = props.survey.themes[props.themeIndex].questions[props.questionIndex];

        let updateTitle = originalQuestion.title;
        const updateTranslations = clone(originalQuestion.translations);
        if(props.selectedLanguageCode === props.survey.languageCode)
        {
            updateTitle = draft.title;
            // Keep translations intact
        }
        else {
            // Keep title intact
            const translationIndex = updateTranslations.findIndex(t => t.languageCode === props.selectedLanguageCode);
            if(translationIndex >= 0)
            {
                updateTranslations[translationIndex].text = draft.title;
            }
        }
        
        const result = await updateSurveyQuestion(
            props.survey.id, 
            props.themeIndex, 
            props.questionIndex, 
            draft.selectedIndicators, 
            updateTitle,
            updateTranslations,
            draft.canComment,
            config,
            draft.conditionalDisplay
        );

        if(result.isSuccess)
        {
            setErrorMessage("");
            props.onSurveyUpdated();
        }
        else
        {
            if(result.errorMessage === 'updateSurveyQuestion_conditional_value_in_use')
            {
                setErrorMessage("Et efterfølgende spørgsmål afhænger af svarmulighederne, og de kan derfor ikke ændres.");    
            }
            else
                setErrorMessage("Fejl ved opdatering af spørgsmål");
        }  
    }

    return (
        <QuestionEditModal
            errorMessage={errorMessage}
            survey={props.survey}
            themeIndex={props.themeIndex}
            draft={draft}
            // No use for opening new question model is editing is disabled
            disabled={false}
            onDraftChange={(draft) => setDraft(draft)}
            onClose={props.onClose}
            onSave={handleSaveQuestion} />
    );
}